/**
 * Normalized viewer-aware projection reducer (§8-§10 гайда).
 *
 * Чистые функции без socket и без React. Инварианты, которые здесь держатся:
 *
 * - identity и dedup — только `senderMessageId`;
 * - `messageId`/`sequence`/`sentAt`/text/name/avatar никогда не участвуют в
 *   identity, dedup или ordering;
 * - global sort объединённого projection не выполняется никогда;
 * - history page order и live receive FIFO — separate partial orders;
 * - tombstone precedence сильнее active record независимо от arrival order.
 */
import { ChatRecord } from "./types";

export interface ProjectionState {
  /** Display placement: identity в локально закреплённом порядке. */
  order: string[];
  /** Записи и tombstones по identity. */
  records: Record<string, ChatRecord>;
}

export const EMPTY_PROJECTION: ProjectionState = { order: [], records: {} };

function tombstoneFrom(senderMessageId: string, deletedAt: number): ChatRecord {
  // Tombstone очищает text, player_name и avatar (§10).
  return {
    senderMessageId,
    sentAt: 0,
    playerName: "",
    avatar: "",
    text: "",
    deleted: true,
    deletedAt,
  };
}

/**
 * Merge записи в существующую позицию. Если менять нечего, возвращает тот же
 * объект — вызывающий по ссылочному равенству понимает, что state не изменился.
 */
function mergeInPlace(existing: ChatRecord, incoming: ChatRecord): ChatRecord {
  // Terminal tombstone: no resurrection, content не восстанавливается.
  if (existing.deleted) {
    const deletedAt = Math.max(existing.deletedAt, incoming.deletedAt);
    return deletedAt === existing.deletedAt
      ? existing
      : { ...existing, deletedAt };
  }
  // Incoming tombstone поглощает active record.
  if (incoming.deleted) {
    return tombstoneFrom(
      existing.senderMessageId,
      Math.max(existing.deletedAt, incoming.deletedAt)
    );
  }
  // Duplicate active identity: позиция и содержимое сохраняются.
  return existing;
}

/**
 * Добавление одной записи в конец projection: live frame или replay буфера.
 * Duplicate identity merge выполняется на месте, без repositioning.
 */
export function appendRecord(
  state: ProjectionState,
  incoming: ChatRecord
): ProjectionState {
  const id = incoming.senderMessageId;
  if (!id) return state;

  const existing = state.records[id];
  if (existing) {
    const merged = mergeInPlace(existing, incoming);
    if (merged === existing) return state;
    return { order: state.order, records: { ...state.records, [id]: merged } };
  }

  return {
    order: [...state.order, id],
    records: { ...state.records, [id]: incoming },
  };
}

/**
 * Base из latest-history page. Records применяются oldest-to-newest в
 * response-array order (§9.1).
 */
export function buildBase(records: ChatRecord[]): ProjectionState {
  return records.reduce(appendRecord, EMPTY_PROJECTION);
}

/**
 * Older page добавляется как один block перед уже загруженным projection
 * (§8). Записи, уже присутствующие в projection, merge на месте и в block не
 * попадают — это сохраняет их исходное placement.
 */
export function prependBlock(
  state: ProjectionState,
  page: ChatRecord[]
): ProjectionState {
  const blockOrder: string[] = [];
  const records = { ...state.records };
  const seenInBlock = new Set<string>();

  for (const incoming of page) {
    const id = incoming.senderMessageId;
    if (!id) continue;

    const existing = records[id];
    if (existing) {
      const merged = mergeInPlace(existing, incoming);
      if (merged !== existing) records[id] = merged;
      continue;
    }
    if (seenInBlock.has(id)) continue;

    seenInBlock.add(id);
    blockOrder.push(id);
    records[id] = incoming;
  }

  if (blockOrder.length === 0 && records === state.records) return state;
  return { order: [...blockOrder, ...state.order], records };
}

/**
 * Authoritative terminal delete. Delete-before-active создаёт tombstone
 * terminal state; duplicate delete idempotent и сохраняет `max(deleted_at)`.
 * Tombstone не перемещает record в display projection.
 */
export function applyTombstone(
  state: ProjectionState,
  senderMessageId: string,
  deletedAt: number
): ProjectionState {
  if (!senderMessageId) return state;

  const existing = state.records[senderMessageId];
  if (!existing) {
    return {
      order: [...state.order, senderMessageId],
      records: {
        ...state.records,
        [senderMessageId]: tombstoneFrom(senderMessageId, deletedAt),
      },
    };
  }

  const nextDeletedAt = Math.max(existing.deletedAt, deletedAt);
  if (existing.deleted && existing.deletedAt === nextDeletedAt) return state;

  return {
    order: state.order,
    records: {
      ...state.records,
      [senderMessageId]: tombstoneFrom(senderMessageId, nextDeletedAt),
    },
  };
}

/** Projection в порядке отображения. */
export function toDisplayList(state: ProjectionState): ChatRecord[] {
  const list: ChatRecord[] = [];
  for (const id of state.order) {
    const record = state.records[id];
    if (record) list.push(record);
  }
  return list;
}
